<template>
	<div class="upcoming-buybox buybox-selector">
		<ListUpcoming
			v-for="(release, index) in upcomingReleases"
			:key="`${release.offer.package?.id}_${release.offer?.package?.packageId}_${index}`"
			:upcomingRelease="release"
			:title="title"
			:filter="filter"
			:isTitleInWatchlist="isTitleInWatchlist"
			:additionalContexts="additionalContexts"
			@onNotifyMe="emit('onNotifyMe')"
		/>
		<template v-if="hasUpcomingReleases || (isInternationalOffersActive && !hasInternationalOffers)">
			<PromotionPlacement
				class="offer-container"
				key="promotion-with-upcoming-or-int"
				:showFreeTrial="showFreeTrial"
				:isFreeBuyBox="filter === 'free'"
				:additionalContexts="additionalContexts"
				:trackingContexts="[]"
				:title="title"
				:offers="[]"
				:seasonNumber="seasonNumber"
			/>

			<NotifyMe
				class="offer-container"
				:title="title"
				:isTitleInWatchlist="isTitleInWatchlist"
				:filter="filter"
				:additionalContexts="additionalContexts"
				@onNotifyMe="emit('onNotifyMe')"
			/>
		</template>
		<template v-else-if="!isInternationalOffersActive">
			<!-- on "free" filtered buybox, we show the promotion row first -->
			<PromotionPlacement
				v-if="filter === 'free'"
				class="offer-container"
				:showFreeTrial="showFreeTrial"
				:isFreeBuyBox="true"
				:additionalContexts="additionalContexts"
				:trackingContexts="[]"
				:title="title"
				:offers="[]"
				:seasonNumber="seasonNumber"
			/>

			<NoOffers
				class="offer-container"
				:title="title"
				:isTitleInWatchlist="isTitleInWatchlist"
				:filter="filter"
				:additionalContexts="additionalContexts"
				@onNotifyMe="emit('onNotifyMe')"
			/>

			<PromotionPlacement
				v-if="filter !== 'free'"
				class="offer-container"
				:showFreeTrial="showFreeTrial"
				:isFreeBuyBox="false"
				:additionalContexts="additionalContexts"
				:trackingContexts="[]"
				:title="title"
				:offers="[]"
				:seasonNumber="seasonNumber"
			/>
		</template>
	</div>
</template>

<script setup lang="ts">
import NoOffers from '@/features/buybox/components/list/NoOffers.vue';
import NotifyMe from '@/features/buybox/components/list/NotifyMe.vue';
import ListUpcoming from '@/features/buybox/components/list/ListUpcoming.vue';
import type { UpcomingReleaseBuybox } from '@/features/buybox/types/types.ts';
import { useInternationalOffersStatus } from '@/features/international-offers/composables/useInternationalOffers';
import { allowedPromotionLanguages, usePromotion } from '@/helpers/composables/usePromotion';
import { SnowplowContext } from '@/helpers/tracking/providers';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { BuyBoxFilterOptions } from '@/stores/useUserStoreTypes';
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';

const PromotionPlacement = defineAsyncComponent(
	() => import('@/features/buybox/components/list/PromotionPlacement.vue')
);

const props = defineProps<{
	upcomingReleases: UpcomingReleaseBuybox[];
	hasUpcomingReleases: boolean;
	title: TitleDetail;
	isTitleInWatchlist?: boolean;
	filter: BuyBoxFilterOptions;
	additionalContexts: SnowplowContext[];
	seasonNumber?: number | null;
}>();

const emit = defineEmits<{
	(e: 'onNotifyMe'): void;
}>();

const { language } = storeToRefs(useLanguageStore());
const { isActive: isInternationalOffersActive, hasInternationalOffers } = useInternationalOffersStatus({
	title: () => props.title,
});
const { promotionOffer } = usePromotion({
	title: () => props.title,
	offers: () => [],
});

const showFreeTrial = computed(
	() =>
		!!promotionOffer.value &&
		[BuyBoxFilterOptions.ALL, BuyBoxFilterOptions.FREE, BuyBoxFilterOptions.SUBSCRIPTION].includes(props.filter) &&
		// splitting language for locale specific ones like Spanish - Spain, Spanish - Latin America, etc
		allowedPromotionLanguages.includes(language.value.split('-')[0])
);
</script>

<style>
.upcoming-buybox {
	.offer-container:nth-child(2n) {
		background: #111924;
	}

	.offer-container:nth-child(2n-1) {
		background: #0c131e;
	}
}
</style>
